import classNames from "classnames";
import { observer } from "mobx-react";
import React from "react";
import { ContentView } from "react-mvvm";
import { ActivityIndicator } from "../../shared/components/activityIndicator/ActivityIndicator";
import { SectionArticleListBlock } from "../../shared/components/blocks/SectionArticleListBlock/SectionArticleListBlock";
import { SectionIconListBlock } from "../../shared/components/blocks/SectionIconListBlock/SectionIconListBlock";
import { SectionImageBlock } from "../../shared/components/blocks/SectionImageBlock/SectionImageBlock";
import { SectionMultiQuoteBlock } from "../../shared/components/blocks/SectionMultiQuoteBlock/SectionMultiQuoteBlock";
import { SectionQuoteBlock } from "../../shared/components/blocks/SectionQuoteBlock/SectionQuoteBlock";
import { SectionTextBlock } from "../../shared/components/blocks/SectionTextBlock/SectionTextBlock";
import { CoursesSection } from "../../shared/components/coursesSection/CoursesSection";
import { PageTitle } from "../../shared/components/pageTitle/PageTitle";
import { useTranslations } from "../../shared/contexts/StaticTranslationContext";
import { SectionBlockTypeDto } from "../../types/shared/dto/SectionBlockTypeDto";
import { ProductViewModel } from "./ProductViewModel";
import { ProductHero } from "./productHero/ProductHero";

export const ProductView = observer(({ model }: { model: ProductViewModel }) => {
  const translations = useTranslations();

  if (!model.productSections) {
    return <article className="Product" />;
  }

  const { productSections, productHeroVM, product, courses, isLoading, goToCourse, coursesSectionId } = model;
  const { title, productParams, seoTitle, seoDescription } = product;

  return (
    <ContentView content={model.childPage}>
      <PageTitle title={seoTitle || title} description={seoDescription}>
        <article className="Product">
          <ActivityIndicator className="Product__loader" isLoading={isLoading}>
            <div className="Product__hero">{productHeroVM && <ProductHero model={productHeroVM} />}</div>
            {courses && (
              <CoursesSection
                courses={courses}
                coursesSectionId={coursesSectionId}
                sectionTitle={"courses"}
                productName={title}
                onCourseClick={goToCourse}
              />
            )}
            {productSections && (
              <div className="Product__content">
                <ul className="Product__blocksList">
                  {productSections.map((block, i) => {
                    const nextBlockType =
                      i + 1 < productSections.length ? productSections[i + 1].sectionBlockType : null;
                    return (
                      <li
                        key={block.id}
                        className={classNames(
                          {
                            "Product__sectionItem--nextQuoteBlock":
                              nextBlockType === SectionBlockTypeDto.SectionQuoteBlock ||
                              nextBlockType === SectionBlockTypeDto.SectionMultipleQuotesBlock,
                          },
                          {
                            "Product__sectionItem--lastImageBlock":
                              nextBlockType &&
                              nextBlockType !== SectionBlockTypeDto.SectionImageBlock &&
                              block.sectionBlockType === SectionBlockTypeDto.SectionImageBlock,
                          },
                          {
                            "Product__sectionItem--textBlockWithImageBlockNext":
                              nextBlockType === SectionBlockTypeDto.SectionImageBlock &&
                              block.sectionBlockType === SectionBlockTypeDto.SectionTextBlock,
                          }
                        )}
                      >
                        {block.sectionBlockType === SectionBlockTypeDto.SectionTextBlock && block.sectionTextBlock && (
                          <SectionTextBlock key={block.id} id={block.id} {...block.sectionTextBlock} />
                        )}
                        {block.sectionBlockType === SectionBlockTypeDto.SectionIconListBlock &&
                          block.sectionIconListBlock && (
                            <SectionIconListBlock key={block.id} {...block.sectionIconListBlock} />
                          )}
                        {block.sectionBlockType === SectionBlockTypeDto.SectionImageBlock &&
                          block.sectionImageBlock && (
                            <SectionImageBlock
                              key={block.id}
                              id={block.id}
                              {...block.sectionImageBlock}
                              productParams={productParams}
                            />
                          )}
                        {block.sectionBlockType === SectionBlockTypeDto.SectionQuoteBlock &&
                          block.sectionQuoteBlock && (
                            <SectionQuoteBlock key={block.id} id={block.id} {...block.sectionQuoteBlock} />
                          )}
                        {block.sectionBlockType === SectionBlockTypeDto.SectionMultipleQuotesBlock &&
                          block.sectionMultipleQuotesBlock && (
                            <SectionMultiQuoteBlock
                              key={block.id}
                              id={block.id}
                              {...block.sectionMultipleQuotesBlock}
                            />
                          )}
                        {block.sectionBlockType === SectionBlockTypeDto.SectionArticleListBlock &&
                          block.sectionArticleListBlock && (
                            <SectionArticleListBlock
                              key={block.id}
                              {...block.sectionArticleListBlock}
                              goBackText={translations.goToHomePage}
                              productParams={productParams}
                            />
                          )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </ActivityIndicator>
        </article>
      </PageTitle>
    </ContentView>
  );
});
